import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, NavLink } from 'react-router-dom';

import { ButtonStyle } from './ButtonStyle';

const Button = ({
  type,
  theme,
  size,
  activeTheme,
  icon,
  iconPosition,
  text,
  textAlign,
  to,
  disabled,
  transition,
  error,
  onClick,
}) => {
  const [className, setClassName] = useState('button-element');

  useEffect(() => {
    const currentClassName = ['button-element'];

    if (theme && theme.length) {
      currentClassName.push(`${theme}-theme`);
    }

    if (size && size.length) {
      currentClassName.push(`${size}-size`);
    }

    if (icon && (!text || (text && !text.length))) {
      currentClassName.push('icon-only');
    }

    if (icon && text && text.length) {
      currentClassName.push('icon-text');
    }

    if (icon && iconPosition && iconPosition.length) {
      currentClassName.push(`icon-${iconPosition}`);
    }

    if (textAlign && textAlign.length) {
      currentClassName.push(`text-align-${textAlign}`);
    }

    if (transition) {
      currentClassName.push('with-transition');
    }

    setClassName(currentClassName.join(' '));
  }, [theme, icon, iconPosition, text, textAlign, transition, size]);

  const hasIconOrText = useMemo(
    () => !!icon || !!text.length,
    [icon, text.length]
  );

  if (type === 'button' && hasIconOrText && !!onClick) {
    return (
      <ButtonStyle>
        <button
          type="button"
          className={className}
          disabled={disabled}
          onClick={onClick}
        >
          {icon ? <span className="icon">{icon}</span> : null}
          {text && text.length ? <span className="text">{text}</span> : null}
        </button>
      </ButtonStyle>
    );
  }

  if (type === 'link' && hasIconOrText && !!to.length) {
    return (
      <ButtonStyle>
        <Link to={to} className={className}>
          {icon ? <span className="icon">{icon}</span> : null}
          {text && text.length ? <span className="text">{text}</span> : null}
        </Link>
      </ButtonStyle>
    );
  }

  if (type === 'nav-link' && hasIconOrText && !!to.length) {
    return (
      <ButtonStyle>
        <NavLink
          to={to}
          className={className}
          activeClassName={`${activeTheme}-active-theme`}
          exact
        >
          {icon ? <span className="icon">{icon}</span> : null}
          {text && text.length ? <span className="text">{text}</span> : null}
        </NavLink>
      </ButtonStyle>
    );
  }

  if (type === 'submit' && hasIconOrText && !!onClick) {
    return (
      <ButtonStyle>
        <input
          type={type}
          className={className}
          value={text}
          onClick={onClick}
        />
        {error && error.length ? (
          <p className="feedback-error">{error}</p>
        ) : null}
      </ButtonStyle>
    );
  }

  if (type === 'external-link' && hasIconOrText && !!to.length) {
    return (
      <ButtonStyle>
        <a href={to} className={className} target="_blank" rel="noreferrer">
          {icon ? <span className="icon">{icon}</span> : null}
          {text && text.length ? <span className="text">{text}</span> : null}
        </a>
      </ButtonStyle>
    );
  }

  return null;
};

Button.propTypes = {
  type: PropTypes.oneOf([
    'button',
    'link',
    'nav-link',
    'submit',
    'external-link',
  ]),
  theme: PropTypes.string,
  size: PropTypes.string,
  activeTheme: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.node]),
  iconPosition: PropTypes.string,
  text: PropTypes.string,
  textAlign: PropTypes.string,
  to: PropTypes.string,
  disabled: PropTypes.bool,
  transition: PropTypes.bool,
  error: PropTypes.string,
  onClick: PropTypes.oneOfType([PropTypes.oneOf([null]), PropTypes.func]),
};

Button.defaultProps = {
  type: 'button',
  theme: 'default',
  size: 'default',
  activeTheme: 'default',
  icon: null,
  iconPosition: 'left',
  text: '',
  textAlign: 'center',
  to: '',
  disabled: false,
  transition: false,
  error: '',
  onClick: null,
};

export default Button;
